import { useState, useEffect, useMemo } from 'react';
import { useSessionState } from '@/hooks/useSessionState';
import { SessionKey } from '@/types/storage';
import { useScreenState } from '@/hooks/useScreenState';
import { useAuth } from '@/hooks/useAuth';
import { useOrchestrator } from '@/hooks/useOrchestrator';
import { useScheduledJobsQuery, useJobRunsQuery } from '@/hooks/queries';
import { ScreenType } from '@/types/screen.types';
import { Action } from '@/business/types/action.types';
import { ResponseCode } from '@/business/types/response-codes.types';
import { executeQuery } from '@/lib/execute-query';
import { queryClient } from '@/lib/query-client';
import { queryKeys } from '@/lib/query-keys';
import { logger } from '@/utils/logger';
import { formatTimestamp } from '@/utils/date';
import { generateDeepLink, DeepLinkEntity } from '@/utils/deep-linking';
import { useThemeColors, withOpacity } from '@/styles';
import { hexToRgba } from '@/styles/colors/theme-helpers';
import { spacing } from '@/styles/design-tokens';
import { Button } from '@/components/ui/button';
import { AsyncButton } from '@/components/ui/async-button';
import { IconName } from '@/icons';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { JobType, JobRunStatus } from '@/types/enums';
import { useActiveOrganization } from '@/hooks/useActiveOrganization';
import { useBoardTemplatesQuery } from '@/hooks/queries';
import {
  getCurrentWeek,
  getPreviousWeek,
  formatWeek,
  type WeekInfo,
} from '@/utils/week';
import type {
  GetScheduledJobsJobsItem,
  GetScheduledJobsOutput,
  CreateScheduledJobInput,
  CreateScheduledJobOutput,
  UpdateScheduledJobInput,
  UpdateScheduledJobOutput,
  RunScheduledJobInput,
  RunScheduledJobOutput,
  GetJobRunsInput,
  GetJobRunsOutput,
  GetJobRunsRunsItem,
  CancelJobRunInput,
  CancelJobRunOutput,
} from '@/types/generated';

const JOB_TYPE_LABELS: Record<string, string> = {
  [JobType.WEEKLY_CARRY_OVER]: 'Move Unfinished Tasks to Next Week',
  [JobType.SYNC_BOARD_TEMPLATES]: 'Update Boards from Templates',
  [JobType.ENSURE_USER_BOARDS]: 'Set Up Missing User Boards',
  [JobType.SYNC_COHORTS]: 'Sync Cohort Assignments to Students',
  [JobType.DEADLINE_REMINDERS]: 'Send Deadline Reminders',
};

const JOB_TYPE_DESCRIPTIONS: Record<string, string> = {
  [JobType.WEEKLY_CARRY_OVER]:
    'Copies unfinished tasks from the previous week into the current week so nothing gets lost.',
  [JobType.SYNC_BOARD_TEMPLATES]:
    'Applies the latest template settings (columns) to all boards that are based on a template.',
  [JobType.ENSURE_USER_BOARDS]:
    'Checks that every user has their personal board set up and creates any that are missing.',
  [JobType.SYNC_COHORTS]:
    "Pushes this week's cohort board tasks to every student in the group. New tasks are created; existing ones are updated.",
  [JobType.DEADLINE_REMINDERS]:
    'Notifies all task subscribers when a deadline is approximately 2 days away.',
};

const RUN_ERROR_MESSAGES: Record<string, string> = {
  USER_NOT_AUTHENTICATED: 'You must be logged in to run jobs.',
  JOB_NOT_FOUND: 'This job no longer exists. Refresh the page.',
  JOB_ALREADY_RUNNING: 'This job is already running. Wait for it to finish.',
  FORBIDDEN: 'You do not have permission to run this job.',
  VALIDATION_FAILED: 'Invalid request. Please try again.',
  INTERNAL_SERVER_ERROR: 'Something went wrong on the server. Try again later.',
};

enum ScheduleFrequency {
  DAILY = 'daily',
  WEEKLY = 'weekly',
}

const FREQUENCY_LABELS: Record<ScheduleFrequency, string> = {
  [ScheduleFrequency.DAILY]: 'Daily',
  [ScheduleFrequency.WEEKLY]: 'Weekly',
};

const DAYS_OF_WEEK = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: String(i),
  label:
    i === 0
      ? '12:00 AM'
      : i < 12
        ? `${i}:00 AM`
        : i === 12
          ? '12:00 PM'
          : `${i - 12}:00 PM`,
}));

function buildCronExpression(
  frequency: ScheduleFrequency,
  day: string,
  hour: string
): string {
  const h = parseInt(hour, 10);
  if (frequency === ScheduleFrequency.DAILY) {
    return `0 ${h} * * *`;
  }
  return `0 ${h} * * ${day}`;
}

function describeCron(cron: string): string {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return cron;

  const [, hourStr, , , dowStr] = parts;
  const hour = parseInt(hourStr, 10);
  const hourLabel = isNaN(hour)
    ? hourStr
    : hour === 0
      ? '12:00 AM'
      : hour < 12
        ? `${hour}:00 AM`
        : hour === 12
          ? '12:00 PM'
          : `${hour - 12}:00 PM`;

  if (dowStr === '*') {
    return `Daily at ${hourLabel}`;
  }

  const dayNames = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  const dayIndex = parseInt(dowStr, 10);
  const dayLabel = isNaN(dayIndex) ? dowStr : dayNames[dayIndex] || dowStr;

  return `${dayLabel}s at ${hourLabel}`;
}

function parseCron(cron: string): {
  frequency: ScheduleFrequency;
  day: string;
  hour: string;
} {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) {
    logger.warn(
      'parseCron: unrecognised cron expression, defaulting to daily midnight',
      { cron }
    );
    return { frequency: ScheduleFrequency.DAILY, day: '0', hour: '0' };
  }
  const hour = parts[1] === '*' ? '0' : parts[1];
  const dow = parts[4];
  if (dow === '*')
    return { frequency: ScheduleFrequency.DAILY, day: '0', hour };
  return { frequency: ScheduleFrequency.WEEKLY, day: dow, hour };
}

// ─── Summary helpers ────────────────────────────────────────────────────────

/** Reads a field from the summary, supporting both old flat and new input/output formats */
function readOutput(summary: Record<string, unknown>, field: string): unknown {
  const output = summary.output as Record<string, unknown> | undefined;
  if (output && field in output) return output[field];
  return summary[field];
}

function readInput(summary: Record<string, unknown>, field: string): unknown {
  const input = summary.input as Record<string, unknown> | undefined;
  if (input && field in input) return input[field];
  return undefined;
}

/** Renders job-type-specific inline stats from the summary JSONB */
function RunSummary({
  jobType,
  summary,
}: {
  jobType: string;
  summary?: Record<string, unknown>;
}) {
  if (!summary || Object.keys(summary).length === 0) return <span>-</span>;

  if (jobType === JobType.WEEKLY_CARRY_OVER) {
    const processed = readOutput(summary, 'cards_processed') ?? 0;
    const created = readOutput(summary, 'cards_created') ?? 0;
    const skipped =
      readOutput(summary, 'skipped') === true || summary.skipped === true;

    const sourceWeek = readInput(summary, 'source_week');
    const sourceYear = readInput(summary, 'source_year');
    const targetWeek = readInput(summary, 'target_week');
    const targetYear = readInput(summary, 'target_year');

    const weekLabel =
      sourceWeek != null && targetWeek != null
        ? sourceYear !== targetYear
          ? `W${sourceWeek}/${sourceYear} → W${targetWeek}/${targetYear}`
          : `W${sourceWeek} → W${targetWeek}, ${sourceYear}`
        : null;

    if (skipped)
      return (
        <span className="italic opacity-70">
          Skipped (already ran){weekLabel ? ` | ${weekLabel}` : ''}
        </span>
      );
    return (
      <span className="tabular-nums">
        {weekLabel ? `${weekLabel} | ` : ''}
        {String(processed)} processed, {String(created)} created
      </span>
    );
  }

  if (jobType === JobType.SYSTEM_HEALTH_CHECK) {
    const checked = readOutput(summary, 'components_checked') ?? 0;
    const inserted = readOutput(summary, 'records_inserted') ?? 0;
    return (
      <span className="tabular-nums">
        {String(checked)} components, {String(inserted)} records
      </span>
    );
  }

  if (jobType === JobType.SYNC_BOARD_TEMPLATES) {
    const templates = readOutput(summary, 'templates_checked') ?? 0;
    const updated = readOutput(summary, 'boards_updated') ?? 0;
    const identified = readOutput(summary, 'boards_identified') ?? 0;
    return (
      <span className="tabular-nums">
        {String(templates)} templates, {String(updated)}/{String(identified)}{' '}
        boards synced
      </span>
    );
  }

  if (jobType === JobType.SYNC_COHORTS) {
    const boards = readOutput(summary, 'boards_processed') ?? 0;
    const fannedOut = readOutput(summary, 'fanned_out') ?? 0;
    const synced = readOutput(summary, 'synced') ?? 0;
    return (
      <span className="tabular-nums">
        {String(boards)} boards, {String(fannedOut)} created, {String(synced)}{' '}
        updated
      </span>
    );
  }

  // Fallback
  return (
    <span>
      {Object.entries(summary)
        .filter(([k]) => k !== 'input' && k !== 'output')
        .map(([k, v]) => `${k}: ${v}`)
        .join(', ')}
    </span>
  );
}

// ─── Run Job Dialog ──────────────────────────────────────────────────────────

/** Generates a list of recent weeks for the carry-over week picker */
function getRecentWeeks(count: number): WeekInfo[] {
  const weeks: WeekInfo[] = [];
  let current = getCurrentWeek();
  for (let i = 0; i < count; i++) {
    weeks.push(current);
    current = getPreviousWeek(current);
  }
  return weeks;
}

function RunJobDialog({
  job,
  onConfirm,
  onCancel,
  isRunning,
}: {
  job: GetScheduledJobsJobsItem;
  onConfirm: (params: Record<string, unknown>) => Promise<void>;
  onCancel: () => void;
  isRunning: boolean;
}) {
  const colors = useThemeColors();
  const { activeOrganizationId } = useActiveOrganization();

  // Weekly carry-over state
  const recentWeeks = useMemo(() => getRecentWeeks(8), []);
  const defaultWeekKey = `${recentWeeks[0].week}-${recentWeeks[0].year}`;
  const [selectedWeekKey, setSelectedWeekKey] = useSessionState<string>(
    SessionKey.LB_SCHEDULED_JOBS_SELECTED_WEEK_KEY,
    defaultWeekKey
  );
  const selectedWeek = useMemo(
    () =>
      recentWeeks.find(w => `${w.week}-${w.year}` === selectedWeekKey) ??
      recentWeeks[0],
    [recentWeeks, selectedWeekKey]
  );
  const sourceWeek = useMemo(
    () => getPreviousWeek(selectedWeek),
    [selectedWeek]
  );

  // Sync board templates state
  const { data: templatesData } = useBoardTemplatesQuery({
    orgId:
      job.job_type === JobType.SYNC_BOARD_TEMPLATES
        ? activeOrganizationId
        : undefined,
  });
  const templates = templatesData?.templates ?? [];
  const [persistedTemplateIds, setPersistedTemplateIds] = useSessionState<
    string[]
  >(SessionKey.LB_SCHEDULED_JOBS_SELECTED_TEMPLATE_IDS, []);
  const [selectedTemplateIds, setSelectedTemplateIdsRaw] = useState<
    Set<string>
  >(() => new Set(persistedTemplateIds));
  const setSelectedTemplateIds = (
    updater: ((prev: Set<string>) => Set<string>) | Set<string>
  ) => {
    setSelectedTemplateIdsRaw(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      setPersistedTemplateIds([...next]);
      return next;
    });
  };

  // Initialize selected templates when data loads
  useEffect(() => {
    if (templates.length > 0 && selectedTemplateIds.size === 0) {
      setSelectedTemplateIds(new Set(templates.map(t => t.id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates]);

  const toggleTemplate = (id: string) => {
    setSelectedTemplateIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAllTemplates = () => {
    if (selectedTemplateIds.size === templates.length) {
      setSelectedTemplateIds(new Set());
    } else {
      setSelectedTemplateIds(new Set(templates.map(t => t.id)));
    }
  };

  const handleConfirm = async () => {
    if (job.job_type === JobType.WEEKLY_CARRY_OVER) {
      await onConfirm({
        target_week: selectedWeek.week,
        target_year: selectedWeek.year,
      });
    } else if (job.job_type === JobType.SYNC_BOARD_TEMPLATES) {
      await onConfirm({
        template_ids: Array.from(selectedTemplateIds),
      });
    } else {
      await onConfirm({});
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: hexToRgba(colors.text.primary, 0.4) }}
      onClick={onCancel}
    >
      <div
        className="rounded-lg border p-6 flex flex-col gap-4 w-full max-w-md shadow-xl"
        style={{
          backgroundColor: colors.offWhite,
          borderColor: colors.border.default,
        }}
        onClick={e => e.stopPropagation()}
      >
        <div>
          <span className="font-medium text-sm">
            Run {JOB_TYPE_LABELS[job.job_type] || job.job_type}
          </span>
        </div>

        {/* Weekly Carry-Over inputs */}
        {job.job_type === JobType.WEEKLY_CARRY_OVER && (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-2">
              <label
                className="text-xs"
                style={{ color: colors.text.secondary }}
              >
                Target Week
              </label>
              <Select
                value={selectedWeekKey}
                onValueChange={setSelectedWeekKey}
              >
                <SelectTrigger className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {recentWeeks.map(w => {
                    const key = `${w.week}-${w.year}`;
                    return (
                      <SelectItem key={key} value={key}>
                        Week {w.week}, {w.year} ({formatWeek(w, 'long')})
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div
              className="text-xs px-3 py-2 rounded"
              style={{
                backgroundColor: withOpacity(colors.primary, 0.06),
                color: colors.text.primary,
                border: `1px solid ${withOpacity(colors.primary, 0.12)}`,
              }}
            >
              Will copy incomplete cards from{' '}
              <strong>
                Week {sourceWeek.week}, {sourceWeek.year}
              </strong>{' '}
              to{' '}
              <strong>
                Week {selectedWeek.week}, {selectedWeek.year}
              </strong>
              .
              <br />
              <span style={{ color: colors.text.secondary }}>
                Cards that are not yet completed in the source week will be
                recreated in the target week with the same details and
                assignments.
              </span>
            </div>
          </div>
        )}

        {/* Sync Board Templates inputs */}
        {job.job_type === JobType.SYNC_BOARD_TEMPLATES && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <label
                className="text-xs"
                style={{ color: colors.text.secondary }}
              >
                Templates to sync
              </label>
              <button
                className="text-xs underline"
                style={{ color: colors.primary }}
                onClick={toggleAllTemplates}
              >
                {selectedTemplateIds.size === templates.length
                  ? 'Deselect all'
                  : 'Select all'}
              </button>
            </div>

            {templates.length === 0 ? (
              <div
                className="text-xs py-2"
                style={{ color: colors.text.secondary }}
              >
                No templates found for this organization.
              </div>
            ) : (
              <div className="flex flex-col gap-1 max-h-48 overflow-y-auto">
                {templates.map(t => (
                  <label
                    key={t.id}
                    className="flex items-center gap-2 text-xs py-1.5 px-2 rounded cursor-pointer hover:opacity-80"
                    style={{
                      backgroundColor: selectedTemplateIds.has(t.id)
                        ? withOpacity(colors.primary, 0.06)
                        : 'transparent',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTemplateIds.has(t.id)}
                      onChange={() => toggleTemplate(t.id)}
                      className="accent-current"
                      style={{ accentColor: colors.primary }}
                    />
                    <span className="font-medium">{t.name}</span>
                    {t.description && (
                      <span
                        className="ml-auto text-[10px] truncate max-w-[200px]"
                        style={{ color: colors.text.secondary }}
                        title={t.description}
                      >
                        {t.description}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            )}

            <div
              className="text-xs px-3 py-2 rounded"
              style={{
                backgroundColor: withOpacity(colors.primary, 0.06),
                color: colors.text.primary,
                border: `1px solid ${withOpacity(colors.primary, 0.12)}`,
              }}
            >
              This will sync the selected template
              {selectedTemplateIds.size !== 1 ? 's' : ''} to all boards that use{' '}
              {selectedTemplateIds.size !== 1 ? 'them' : 'it'}.
              <br />
              <span style={{ color: colors.text.secondary }}>
                Board statuses will be updated to match the latest template
                configuration. Boards already up-to-date will be skipped.
              </span>
            </div>
          </div>
        )}

        {/* System Health Check — no special inputs */}
        {job.job_type === JobType.SYSTEM_HEALTH_CHECK && (
          <div
            className="text-xs px-3 py-2 rounded"
            style={{
              backgroundColor: withOpacity(colors.primary, 0.06),
              color: colors.text.secondary,
              border: `1px solid ${withOpacity(colors.primary, 0.12)}`,
            }}
          >
            This will check all system components and record their health
            status.
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <AsyncButton
            size="sm"
            icon={IconName.PLAY}
            onClick={handleConfirm}
            isLoading={isRunning}
            loadingText="Running..."
            disabled={
              isRunning ||
              (job.job_type === JobType.SYNC_BOARD_TEMPLATES &&
                selectedTemplateIds.size === 0)
            }
          >
            Confirm & Run
          </AsyncButton>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function ScheduledJobs() {
  const { user } = useAuth();
  const { activeOrganizationId } = useActiveOrganization();
  const colors = useThemeColors();

  const runStatusColors: Record<string, string> = {
    [JobRunStatus.RUNNING]: colors.primary,
    [JobRunStatus.COMPLETED]: colors.success,
    [JobRunStatus.FAILED]: colors.error,
    [JobRunStatus.PARTIAL]: colors.warning.text,
    cancelled: colors.text.secondary,
  };

  const [expandedJobId, setExpandedJobId] = useSessionState<string | null>(
    SessionKey.LB_SCHEDULED_JOBS_EXPANDED_JOB_ID,
    null
  );

  const { data: jobsData, isLoading: isLoadingJobs } =
    useScheduledJobsQuery(activeOrganizationId);
  const { data: jobRunsData, isLoading: isLoadingRuns } = useJobRunsQuery(
    expandedJobId ?? undefined
  );
  const jobs = jobsData?.jobs ?? [];
  const jobRuns = jobRunsData?.runs ?? [];
  const runsTotal = jobRunsData?.total_count ?? 0;
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createJobType, setCreateJobType] = useSessionState<JobType>(
    SessionKey.LB_SCHEDULED_JOBS_CREATE_JOB_TYPE,
    JobType.WEEKLY_CARRY_OVER
  );
  const [createFrequency, setCreateFrequency] =
    useSessionState<ScheduleFrequency>(
      SessionKey.LB_SCHEDULED_JOBS_CREATE_FREQUENCY,
      ScheduleFrequency.WEEKLY
    );
  const [createDay, setCreateDay] = useSessionState<string>(
    SessionKey.LB_SCHEDULED_JOBS_CREATE_DAY,
    '0'
  );
  const [createHour, setCreateHour] = useSessionState<string>(
    SessionKey.LB_SCHEDULED_JOBS_CREATE_HOUR,
    '0'
  );
  const [createDescription, setCreateDescription] = useSessionState<string>(
    SessionKey.LB_SCHEDULED_JOBS_CREATE_DESCRIPTION,
    ''
  );
  const [createScheduleEnabled, setCreateScheduleEnabled] =
    useSessionState<boolean>(
      SessionKey.LB_SCHEDULED_JOBS_CREATE_ENABLED,
      false
    );

  const computedCron = buildCronExpression(
    createFrequency,
    createDay,
    createHour
  );

  const [editingJobId, setEditingJobId] = useState<string | null>(null);
  const [editScheduleEnabled, setEditScheduleEnabled] = useState(false);
  const [editFrequency, setEditFrequency] = useState<ScheduleFrequency>(
    ScheduleFrequency.WEEKLY
  );
  const [editDay, setEditDay] = useState('0');
  const [editHour, setEditHour] = useState('0');
  const [editDescription, setEditDescription] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const computedEditCron = buildCronExpression(
    editFrequency,
    editDay,
    editHour
  );

  const { execute: createJob, isLoading: isCreating } = useOrchestrator<
    CreateScheduledJobInput,
    CreateScheduledJobOutput
  >(Action.CREATE_SCHEDULED_JOB);

  const { execute: updateJob } = useOrchestrator<
    UpdateScheduledJobInput,
    UpdateScheduledJobOutput
  >(Action.UPDATE_SCHEDULED_JOB);

  const { execute: runJob } = useOrchestrator<
    RunScheduledJobInput,
    RunScheduledJobOutput
  >(Action.RUN_SCHEDULED_JOB);

  const [runningJobId, setRunningJobId] = useState<string | null>(null);
  const [runDialogJobId, setRunDialogJobId] = useState<string | null>(null);
  const [runError, setRunError] = useState<{
    jobId: string;
    code: string;
  } | null>(null);

  const { execute: cancelRun } = useOrchestrator<
    CancelJobRunInput,
    CancelJobRunOutput
  >(Action.CANCEL_JOB_RUN);

  const [killingRunId, setKillingRunId] = useState<string | null>(null);

  const handleToggleEnabled = async (job: GetScheduledJobsJobsItem) => {
    const result = await updateJob({
      job_id: job.id,
      is_enabled: !job.is_enabled,
    });
    if (result.code === ResponseCode.SUCCESS) {
      queryClient.setQueryData<GetScheduledJobsOutput>(
        queryKeys.scheduledJobs.forOrg(activeOrganizationId!),
        old =>
          old
            ? {
                ...old,
                jobs: old.jobs.map(j =>
                  j.id === job.id ? { ...j, is_enabled: !j.is_enabled } : j
                ),
              }
            : old
      );
    }
  };

  const handleExpandJob = (jobId: string) => {
    setExpandedJobId(expandedJobId === jobId ? null : jobId);
  };

  const handleRunClick = (run: GetJobRunsRunsItem) => {
    const url = generateDeepLink(DeepLinkEntity.JOB_RUN, run.id);
    window.open(url, '_blank');
  };

  const handleRunNow = async (
    job: GetScheduledJobsJobsItem,
    extraParams: Record<string, unknown> = {}
  ) => {
    setRunningJobId(job.id);
    setRunError(null);
    const result = await runJob({ job_id: job.id, ...extraParams });
    setRunningJobId(null);
    setRunDialogJobId(null);
    if (result.code === ResponseCode.SUCCESS) {
      queryClient.setQueryData<GetScheduledJobsOutput>(
        queryKeys.scheduledJobs.forOrg(activeOrganizationId!),
        old =>
          old
            ? {
                ...old,
                jobs: old.jobs.map(j =>
                  j.id === job.id
                    ? {
                        ...j,
                        last_run_at: new Date().toISOString(),
                        last_run_status:
                          result.data?.status || JobRunStatus.COMPLETED,
                      }
                    : j
                ),
              }
            : old
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.jobRuns.forJob(job.id),
      });
    } else {
      setRunError({ jobId: job.id, code: result.code });
    }
  };

  const handleKillRun = async (job: GetScheduledJobsJobsItem) => {
    const runsData = await executeQuery<GetJobRunsInput, GetJobRunsOutput>(
      Action.GET_JOB_RUNS,
      { job_id: job.id, page: 1, page_size: 5 }
    ).catch(() => null);
    if (!runsData) return;

    const runningRun = runsData.runs.find(
      r => r.status === JobRunStatus.RUNNING
    );
    if (!runningRun) return;

    setKillingRunId(runningRun.id);
    const result = await cancelRun({ run_id: runningRun.id });
    setKillingRunId(null);

    if (result.code === ResponseCode.SUCCESS) {
      queryClient.setQueryData<GetScheduledJobsOutput>(
        queryKeys.scheduledJobs.forOrg(activeOrganizationId!),
        old =>
          old
            ? {
                ...old,
                jobs: old.jobs.map(j =>
                  j.id === job.id
                    ? { ...j, last_run_status: JobRunStatus.FAILED }
                    : j
                ),
              }
            : old
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.jobRuns.forJob(job.id),
      });
    }
  };

  const handleCreate = async () => {
    const input: Record<string, unknown> = {
      job_type: createJobType,
      description: createDescription || undefined,
      is_enabled: true,
    };
    if (createScheduleEnabled) {
      input.cron_expression = computedCron;
    }
    const result = await createJob(input as CreateScheduledJobInput);
    if (result.code === ResponseCode.SUCCESS) {
      setShowCreateDialog(false);
      // Reset form fields (also clears session keys)
      setCreateJobType(JobType.WEEKLY_CARRY_OVER);
      setCreateFrequency(ScheduleFrequency.WEEKLY);
      setCreateDay('0');
      setCreateHour('0');
      setCreateDescription('');
      setCreateScheduleEnabled(false);
      queryClient.invalidateQueries({
        queryKey: queryKeys.scheduledJobs.forOrg(activeOrganizationId!),
      });
    }
  };

  const openEditDialog = (job: GetScheduledJobsJobsItem) => {
    if (job.cron_expression) {
      const { frequency, day, hour } = parseCron(job.cron_expression);
      setEditScheduleEnabled(true);
      setEditFrequency(frequency);
      setEditDay(day);
      setEditHour(hour);
    } else {
      setEditScheduleEnabled(false);
      setEditFrequency(ScheduleFrequency.WEEKLY);
      setEditDay('0');
      setEditHour('0');
    }
    setEditDescription(job.description ?? '');
    setEditingJobId(job.id);
  };

  const handleUpdate = async (job: GetScheduledJobsJobsItem) => {
    setIsUpdating(true);
    const cronExpression = editScheduleEnabled ? computedEditCron : '';
    const result = await updateJob({
      job_id: job.id,
      cron_expression: cronExpression,
      description: editDescription,
    });
    setIsUpdating(false);
    if (result.code === ResponseCode.SUCCESS) {
      setEditingJobId(null);
      queryClient.setQueryData<GetScheduledJobsOutput>(
        queryKeys.scheduledJobs.forOrg(activeOrganizationId!),
        old =>
          old
            ? {
                ...old,
                jobs: old.jobs.map(j =>
                  j.id === job.id
                    ? {
                        ...j,
                        cron_expression: cronExpression || undefined,
                        description: editDescription,
                      }
                    : j
                ),
              }
            : old
      );
    }
  };

  useScreenState({
    metadata: {
      screenType: ScreenType.SCHEDULED_JOBS,
      requiresAuth: true,
      showHeader: true,
      showSidebar: true,
    },
    onLoad: async (_strategy, state) => {
      logger.info('Jobs: Loading');
      state.setSuccess();
    },
    lifecycle: {
      onUnmount: () => {
        logger.info('Jobs unmounted');
      },
    },
  });

  if (!user) return null;

  // ─── List view ────────────────────────────────────────────────────────
  return (
    <div
      className="flex flex-col h-full"
      style={{
        padding: spacing.lg,
        gap: spacing.lg,
        color: colors.text.primary,
      }}
    >
      <div className="flex flex-col gap-4 w-full">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Jobs</h2>
          <AsyncButton
            onClick={async () => setShowCreateDialog(true)}
            icon={IconName.PLUS}
          >
            Create Job
          </AsyncButton>
        </div>

        {/* Create Dialog */}
        {showCreateDialog && (
          <div
            className="rounded-lg border p-4 flex flex-col gap-3"
            style={{
              backgroundColor: colors.offWhite,
              borderColor: colors.border.default,
            }}
          >
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm">New Job</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCreateDialog(false)}
              >
                Cancel
              </Button>
            </div>
            <div className="flex flex-col gap-2">
              <label
                className="text-xs"
                style={{ color: colors.text.secondary }}
              >
                Job Type
              </label>
              <Select
                value={createJobType}
                onValueChange={v => setCreateJobType(v as JobType)}
              >
                <SelectTrigger className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(JobType)
                    .filter(jt => jt !== JobType.SYSTEM_HEALTH_CHECK)
                    .map(jt => (
                      <SelectItem
                        key={jt}
                        value={jt}
                        className="pl-3 py-2"
                        description={JOB_TYPE_DESCRIPTIONS[jt]}
                      >
                        {JOB_TYPE_LABELS[jt] || jt}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={createScheduleEnabled}
                  onChange={e => setCreateScheduleEnabled(e.target.checked)}
                  className="accent-current"
                  style={{ accentColor: colors.primary }}
                />
                <span
                  className="text-xs"
                  style={{ color: colors.text.secondary }}
                >
                  Enable schedule
                </span>
              </label>
              {createScheduleEnabled && (
                <>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Select
                      value={createFrequency}
                      onValueChange={v =>
                        setCreateFrequency(v as ScheduleFrequency)
                      }
                    >
                      <SelectTrigger className="text-sm w-28">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.values(ScheduleFrequency).map(f => (
                          <SelectItem key={f} value={f}>
                            {FREQUENCY_LABELS[f]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {createFrequency === ScheduleFrequency.WEEKLY && (
                      <>
                        <span
                          className="text-xs"
                          style={{ color: colors.text.secondary }}
                        >
                          on
                        </span>
                        <Select value={createDay} onValueChange={setCreateDay}>
                          <SelectTrigger className="text-sm w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DAYS_OF_WEEK.map(d => (
                              <SelectItem key={d.value} value={d.value}>
                                {d.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </>
                    )}
                    <span
                      className="text-xs"
                      style={{ color: colors.text.secondary }}
                    >
                      at
                    </span>
                    <Select value={createHour} onValueChange={setCreateHour}>
                      <SelectTrigger className="text-sm w-28">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {HOURS.map(h => (
                          <SelectItem key={h.value} value={h.value}>
                            {h.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <span
                    className="text-xs font-mono"
                    style={{ color: colors.text.secondary }}
                  >
                    Cron: {computedCron}
                  </span>
                </>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label
                className="text-xs"
                style={{ color: colors.text.secondary }}
              >
                Description (optional)
              </label>
              <input
                type="text"
                value={createDescription}
                onChange={e => setCreateDescription(e.target.value)}
                className="text-sm px-3 py-2 rounded border outline-none"
                style={{
                  borderColor: colors.border.default,
                  backgroundColor: colors.background,
                  color: colors.text.primary,
                }}
                placeholder="Carry over incomplete cards weekly"
              />
            </div>
            <div className="flex justify-end">
              <AsyncButton
                onClick={handleCreate}
                isLoading={isCreating}
                icon={IconName.SAVE}
                loadingText="Creating..."
              >
                Create
              </AsyncButton>
            </div>
          </div>
        )}

        {/* Jobs List */}
        {isLoadingJobs ? (
          <div className="text-sm" style={{ color: colors.text.secondary }}>
            Loading jobs...
          </div>
        ) : jobs.length === 0 ? (
          <div
            className="text-sm text-center py-8"
            style={{ color: colors.text.secondary }}
          >
            No jobs configured. Create one to run manually or on a schedule.
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {jobs
              .filter(job => job.job_type !== JobType.SYSTEM_HEALTH_CHECK)
              .map(job => (
                <div key={job.id}>
                  {/* Job Row */}
                  <div
                    className="flex items-center gap-4 px-4 py-3 rounded-lg border cursor-pointer transition-all duration-500"
                    style={{
                      borderColor:
                        expandedJobId === job.id
                          ? colors.primary
                          : colors.border.default,
                      backgroundColor: colors.offWhite,
                    }}
                    onClick={() => handleExpandJob(job.id)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">
                          {JOB_TYPE_LABELS[job.job_type] || job.job_type}
                        </span>
                        <span
                          className="text-xs px-2 py-0.5 rounded-full"
                          style={{
                            backgroundColor: job.is_enabled
                              ? withOpacity(colors.success, 0.15)
                              : withOpacity(colors.text.secondary, 0.15),
                            color: job.is_enabled
                              ? colors.success
                              : colors.text.secondary,
                          }}
                        >
                          {job.is_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      <div
                        className="text-xs mt-1 truncate"
                        style={{ color: colors.text.secondary }}
                      >
                        {job.description || JOB_TYPE_DESCRIPTIONS[job.job_type]}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      <div className="text-right">
                        <div
                          className="text-xs"
                          style={{ color: colors.text.secondary }}
                          title={job.cron_expression || 'Manual only'}
                        >
                          {job.cron_expression
                            ? describeCron(job.cron_expression)
                            : 'Manual'}
                        </div>
                        {job.last_run_at && (
                          <div
                            className="text-xs mt-0.5"
                            style={{ color: colors.text.secondary }}
                          >
                            Last: {formatDateTime(job.last_run_at)}
                          </div>
                        )}
                      </div>
                      {/* Run Now / Kill */}
                      <div onClick={e => e.stopPropagation()}>
                        {job.last_run_status === JobRunStatus.RUNNING ||
                        (expandedJobId === job.id &&
                          jobRuns.some(
                            r => r.status === JobRunStatus.RUNNING
                          )) ? (
                          <AsyncButton
                            variant="ghost"
                            size="sm"
                            onClick={() => handleKillRun(job)}
                            isLoading={killingRunId != null}
                            loadingText="Killing..."
                            style={{ color: colors.error }}
                          >
                            Kill
                          </AsyncButton>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setRunDialogJobId(
                                runDialogJobId === job.id ? null : job.id
                              )
                            }
                            disabled={runningJobId === job.id}
                          >
                            {runDialogJobId === job.id ? 'Cancel' : 'Run Now'}
                          </Button>
                        )}
                      </div>
                      {/* Edit */}
                      <div onClick={e => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            editingJobId === job.id
                              ? setEditingJobId(null)
                              : openEditDialog(job)
                          }
                        >
                          {editingJobId === job.id ? 'Cancel' : 'Edit'}
                        </Button>
                      </div>
                      {/* Toggle */}
                      <button
                        className="relative w-10 h-5 rounded-full transition-colors"
                        style={{
                          backgroundColor: job.is_enabled
                            ? colors.primary
                            : colors.border.default,
                        }}
                        onClick={e => {
                          e.stopPropagation();
                          handleToggleEnabled(job);
                        }}
                      >
                        <span
                          className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform"
                          style={{
                            left: job.is_enabled ? '22px' : '2px',
                          }}
                        />
                      </button>
                    </div>
                  </div>

                  {/* Edit dialog */}
                  {editingJobId === job.id && (
                    <div
                      className="rounded-lg border p-4 flex flex-col gap-3 mt-1"
                      style={{
                        backgroundColor: colors.offWhite,
                        borderColor: colors.border.default,
                      }}
                    >
                      <span className="font-medium text-sm">
                        Edit: {JOB_TYPE_LABELS[job.job_type] || job.job_type}
                      </span>
                      <div className="flex flex-col gap-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editScheduleEnabled}
                            onChange={e =>
                              setEditScheduleEnabled(e.target.checked)
                            }
                            className="accent-current"
                            style={{ accentColor: colors.primary }}
                          />
                          <span
                            className="text-xs"
                            style={{ color: colors.text.secondary }}
                          >
                            Enable schedule
                          </span>
                        </label>
                        {editScheduleEnabled && (
                          <>
                            <div className="flex items-center gap-2 flex-wrap">
                              <Select
                                value={editFrequency}
                                onValueChange={v =>
                                  setEditFrequency(v as ScheduleFrequency)
                                }
                              >
                                <SelectTrigger className="text-sm w-28">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {Object.values(ScheduleFrequency).map(f => (
                                    <SelectItem key={f} value={f}>
                                      {FREQUENCY_LABELS[f]}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {editFrequency === ScheduleFrequency.WEEKLY && (
                                <>
                                  <span
                                    className="text-xs"
                                    style={{ color: colors.text.secondary }}
                                  >
                                    on
                                  </span>
                                  <Select
                                    value={editDay}
                                    onValueChange={setEditDay}
                                  >
                                    <SelectTrigger className="text-sm w-32">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {DAYS_OF_WEEK.map(d => (
                                        <SelectItem
                                          key={d.value}
                                          value={d.value}
                                        >
                                          {d.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </>
                              )}
                              <span
                                className="text-xs"
                                style={{ color: colors.text.secondary }}
                              >
                                at
                              </span>
                              <Select
                                value={editHour}
                                onValueChange={setEditHour}
                              >
                                <SelectTrigger className="text-sm w-28">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {HOURS.map(h => (
                                    <SelectItem key={h.value} value={h.value}>
                                      {h.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <span
                              className="text-xs font-mono"
                              style={{ color: colors.text.secondary }}
                            >
                              Cron: {computedEditCron}
                            </span>
                          </>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <label
                          className="text-xs"
                          style={{ color: colors.text.secondary }}
                        >
                          Description (optional)
                        </label>
                        <input
                          type="text"
                          value={editDescription}
                          onChange={e => setEditDescription(e.target.value)}
                          className="text-sm px-3 py-2 rounded border outline-none"
                          style={{
                            borderColor: colors.border.default,
                            backgroundColor: colors.background,
                            color: colors.text.primary,
                          }}
                          placeholder="Carry over incomplete cards weekly"
                        />
                      </div>
                      <div className="flex justify-end">
                        <AsyncButton
                          onClick={() => handleUpdate(job)}
                          isLoading={isUpdating}
                          icon={IconName.SAVE}
                          loadingText="Saving..."
                        >
                          Save
                        </AsyncButton>
                      </div>
                    </div>
                  )}

                  {/* Run error message */}
                  {runError?.jobId === job.id && (
                    <div
                      className="flex items-center gap-2 px-4 py-2 text-xs rounded-md mx-1 mt-1"
                      style={{
                        backgroundColor: withOpacity(colors.error, 0.08),
                        color: colors.error,
                        border: `1px solid ${withOpacity(colors.error, 0.2)}`,
                      }}
                    >
                      <span className="font-medium">Run failed:</span>
                      <span>
                        {RUN_ERROR_MESSAGES[runError.code] ??
                          `Unexpected error (${runError.code})`}
                      </span>
                      <button
                        className="ml-auto text-xs underline opacity-70 hover:opacity-100"
                        onClick={() => setRunError(null)}
                      >
                        dismiss
                      </button>
                    </div>
                  )}

                  {/* Expanded: Job Runs */}
                  {expandedJobId === job.id && (
                    <div
                      className="ml-4 mt-1 rounded-lg border overflow-hidden"
                      style={{
                        borderColor: colors.border.default,
                      }}
                    >
                      <div
                        className="px-4 py-2 text-xs font-medium flex items-center justify-between"
                        style={{
                          backgroundColor: colors.background,
                          color: colors.text.secondary,
                        }}
                      >
                        <span>Run History ({runsTotal})</span>
                      </div>
                      {isLoadingRuns ? (
                        <div
                          className="px-4 py-3 text-xs"
                          style={{ color: colors.text.secondary }}
                        >
                          Loading runs...
                        </div>
                      ) : jobRuns.length === 0 ? (
                        <div
                          className="px-4 py-3 text-xs"
                          style={{ color: colors.text.secondary }}
                        >
                          No runs yet.
                        </div>
                      ) : (
                        <table className="w-full text-xs">
                          <thead>
                            <tr
                              style={{
                                backgroundColor: colors.background,
                                color: colors.text.secondary,
                              }}
                            >
                              <th className="text-left px-4 py-2 font-medium">
                                Status
                              </th>
                              <th className="text-left px-4 py-2 font-medium">
                                Started
                              </th>
                              <th className="text-left px-4 py-2 font-medium">
                                Completed
                              </th>
                              <th className="text-left px-4 py-2 font-medium">
                                Summary
                              </th>
                              <th className="text-left px-4 py-2 font-medium">
                                Error
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {jobRuns.map(run => (
                              <tr
                                key={run.id}
                                className="border-t cursor-pointer hover:opacity-80"
                                style={{ borderColor: colors.border.default }}
                                onClick={() => handleRunClick(run)}
                              >
                                <td className="px-4 py-2">
                                  <span
                                    className="px-2 py-0.5 rounded-full font-medium"
                                    style={{
                                      backgroundColor: withOpacity(
                                        runStatusColors[run.status] ||
                                          colors.text.secondary,
                                        0.12
                                      ),
                                      color:
                                        runStatusColors[run.status] ||
                                        colors.text.secondary,
                                    }}
                                  >
                                    {run.status}
                                  </span>
                                </td>
                                <td
                                  className="px-4 py-2"
                                  style={{ color: colors.text.primary }}
                                >
                                  {formatDateTime(run.started_at)}
                                </td>
                                <td
                                  className="px-4 py-2"
                                  style={{ color: colors.text.secondary }}
                                >
                                  {run.completed_at
                                    ? formatDateTime(run.completed_at)
                                    : '-'}
                                </td>
                                <td
                                  className="px-4 py-2"
                                  style={{ color: colors.text.primary }}
                                >
                                  <RunSummary
                                    jobType={job.job_type}
                                    summary={
                                      run.summary as
                                        | Record<string, unknown>
                                        | undefined
                                    }
                                  />
                                </td>
                                <td
                                  className="px-4 py-2 max-w-[200px] truncate"
                                  style={{ color: colors.error }}
                                  title={run.error_details || undefined}
                                >
                                  {run.error_details || '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Run Job Modal */}
      {runDialogJobId &&
        (() => {
          const dialogJob = jobs.find(j => j.id === runDialogJobId);
          if (!dialogJob) return null;
          return (
            <RunJobDialog
              job={dialogJob}
              onConfirm={async params => {
                await handleRunNow(dialogJob, params);
              }}
              onCancel={() => setRunDialogJobId(null)}
              isRunning={runningJobId === dialogJob.id}
            />
          );
        })()}
    </div>
  );
}

function formatDateTime(iso: string): string {
  return formatTimestamp(iso, 'datetime') || iso;
}
